<script setup>
// CRIAR / EDITAR HORÁRIO DE ATENDIMENTO (telefonia, etapa E — 25/09/2026).
// Faixas por dia da semana (dá para ter almoço: 08–12 e 13–18), feriados (valem
// todo ano, pelo dia e mês) e para onde vai a ligação dentro e fora do horário.
// Horário de Brasília — a central decide sozinha, sem perguntar ao advup.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import AdvupSelect from 'dashboard/components-next/AdvupSelect.vue';
import AdvupDateTimeInput from 'dashboard/components-next/AdvupDateTimeInput.vue';
import { DIAS, opcoesDeDestino } from './destinos';

const props = defineProps({
  horario: { type: Object, default: null },
  contexto: { type: Object, required: true },
  salvando: { type: Boolean, default: false },
  erro: { type: String, default: '' },
});
const emit = defineEmits(['close', 'salvar']);

const { t } = useI18n();
const T = 'TELEFONIA.ROTEAMENTO.HORARIO';
const editando = computed(() => Boolean(props.horario));

const COMERCIAL = () => ({
  dias: ['mon', 'tue', 'wed', 'thu', 'fri'],
  de: '08:00',
  ate: '18:00',
});

const nome = ref(props.horario?.nome || '');
const faixas = ref(
  props.horario?.faixas?.length
    ? props.horario.faixas.map(f => ({ ...f, dias: [...f.dias] }))
    : [COMERCIAL()]
);
const feriados = ref([...(props.horario?.feriados || [])].sort());
const novoFeriado = ref('');
const aberto = ref(props.horario?.aberto || '');
const fechado = ref(props.horario?.fechado || '');
const tentou = ref(false);

const alternarDia = (faixa, dia) => {
  const i = faixa.dias.indexOf(dia);
  if (i >= 0) faixa.dias.splice(i, 1);
  else faixa.dias.push(dia);
  faixa.dias.sort((a, b) => DIAS.indexOf(a) - DIAS.indexOf(b));
};
const maisFaixa = () =>
  faixas.value.push({ dias: ['sat'], de: '08:00', ate: '12:00' });
const tirarFaixa = i => faixas.value.splice(i, 1);

const acrescentarFeriado = () => {
  const d = novoFeriado.value;
  if (d && !feriados.value.includes(d)) {
    feriados.value = [...feriados.value, d].sort();
  }
  novoFeriado.value = '';
};
const tirarFeriado = d => {
  feriados.value = feriados.value.filter(x => x !== d);
};
const dataCurta = d =>
  new Date(`${d}T12:00:00`).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
  });

const opcoes = computed(() =>
  opcoesDeDestino(props.contexto, t, {
    excluir: props.horario ? `horario:${props.horario.id}` : '',
  })
);

const faixaInvalida = f => !f.dias.length || !f.de || !f.ate || f.de >= f.ate;
const erroGeral = computed(() => {
  if (!nome.value.trim()) return t('TELEFONIA.ROTEAMENTO.FILA.NOME_FALTA');
  if (!faixas.value.length || faixas.value.some(faixaInvalida))
    return t(`${T}.FAIXA_INVALIDA`);
  if (!aberto.value || !fechado.value) return t(`${T}.DESTINOS_FALTAM`);
  return '';
});

function salvar() {
  tentou.value = true;
  if (erroGeral.value) return;
  emit('salvar', {
    name: nome.value.trim(),
    ranges: faixas.value,
    holidays: feriados.value,
    open_destination: aberto.value,
    closed_destination: fechado.value,
  });
}
</script>

<template>
  <div class="advup-modal-fundo" @click.self="emit('close')">
    <div v-modal-acessivel="() => emit('close')" class="advup-modal tel-hor">
      <div class="advup-modal__topo advup-modal__topo--teal">
        <span class="advup-modal__selo" aria-hidden="true">
          <i class="i-ph-clock-fill" />
        </span>
        <div class="min-w-0 flex-1">
          <h3 class="advup-modal__titulo">
            {{
              editando
                ? t(`${T}.EDITAR`, { nome: horario.nome })
                : t(`${T}.NOVO`)
            }}
          </h3>
          <p class="advup-modal__sub">{{ t(`${T}.SUB`) }}</p>
        </div>
        <button
          type="button"
          class="advup-modal__fechar"
          :aria-label="t('TELEFONIA.ROTEAMENTO.CANCELAR')"
          @click="emit('close')"
        >
          <i class="i-lucide-x" />
        </button>
      </div>

      <form class="advup-modal__corpo tel-hor__corpo" @submit.prevent="salvar">
        <label class="tel-hor__campo">
          <span class="advup-rotulo">{{
            t('TELEFONIA.ROTEAMENTO.FILA.NOME')
          }}</span>
          <input
            v-model="nome"
            name="nome-do-horario"
            type="text"
            maxlength="60"
            autocomplete="off"
            class="advup-campo reset-base"
            :placeholder="t(`${T}.NOME_PH`)"
          />
        </label>

        <div class="tel-hor__campo">
          <span class="advup-rotulo">{{ t(`${T}.FAIXAS`) }}</span>
          <span class="advup-nota">{{ t(`${T}.FAIXAS_DICA`) }}</span>
          <TransitionGroup
            tag="ul"
            name="tel-hor-faixa"
            class="tel-hor__faixas"
          >
            <li
              v-for="(f, i) in faixas"
              :key="i"
              class="tel-hor__faixa"
              :class="{ 'tel-hor__faixa--erro': tentou && faixaInvalida(f) }"
            >
              <div
                class="tel-hor__dias"
                role="group"
                :aria-label="t(`${T}.DIAS_ROTULO`)"
              >
                <button
                  v-for="d in DIAS"
                  :key="d"
                  type="button"
                  class="tel-hor__dia"
                  :class="{ 'tel-hor__dia--sim': f.dias.includes(d) }"
                  :aria-pressed="String(f.dias.includes(d))"
                  :aria-label="t(`${T}.DIAS_LONGOS.${d}`)"
                  @click="alternarDia(f, d)"
                >
                  {{ t(`${T}.DIAS.${d}`) }}
                </button>
              </div>
              <div class="tel-hor__horas">
                <input
                  v-model="f.de"
                  type="time"
                  :name="`de-${i}`"
                  class="advup-campo reset-base"
                  :aria-label="t(`${T}.DE`)"
                />
                <span aria-hidden="true">{{ t(`${T}.ATE_CURTO`) }}</span>
                <input
                  v-model="f.ate"
                  type="time"
                  :name="`ate-${i}`"
                  class="advup-campo reset-base"
                  :aria-label="t(`${T}.ATE`)"
                />
                <button
                  type="button"
                  class="advup-acao advup-acao--perigo"
                  :disabled="faixas.length === 1"
                  :aria-label="t(`${T}.TIRAR_FAIXA`)"
                  @click="tirarFaixa(i)"
                >
                  <i class="i-hugeicons-cancel-01" aria-hidden="true" />
                </button>
              </div>
            </li>
          </TransitionGroup>
          <button
            type="button"
            class="advup-btn--vazado tel-hor__mais"
            @click="maisFaixa"
          >
            <i class="i-ph-plus-bold" aria-hidden="true" />
            {{ t(`${T}.MAIS_FAIXA`) }}
          </button>
        </div>

        <div class="tel-hor__campo">
          <span class="advup-rotulo">{{ t(`${T}.FERIADOS`) }}</span>
          <span class="advup-nota">{{ t(`${T}.FERIADOS_DICA`) }}</span>
          <div class="tel-hor__feriados">
            <TransitionGroup name="tel-hor-faixa">
              <span v-for="d in feriados" :key="d" class="tel-hor__feriado">
                <i class="i-ph-calendar-x-fill" aria-hidden="true" />
                {{ dataCurta(d) }}
                <button
                  type="button"
                  class="tel-hor__feriado-x"
                  :aria-label="t(`${T}.TIRAR_FERIADO`, { dia: dataCurta(d) })"
                  @click="tirarFeriado(d)"
                >
                  <i class="i-lucide-x" aria-hidden="true" />
                </button>
              </span>
            </TransitionGroup>
          </div>
          <div class="tel-hor__novo-feriado">
            <AdvupDateTimeInput
              v-model="novoFeriado"
              date-only
              class="tel-hor__data"
              :rotulo="t(`${T}.FERIADOS`)"
              @keydown.enter.prevent="acrescentarFeriado"
            />
            <button
              type="button"
              class="advup-btn--vazado"
              :disabled="!novoFeriado"
              @click="acrescentarFeriado"
            >
              {{ t(`${T}.MAIS_FERIADO`) }}
            </button>
          </div>
        </div>

        <div class="tel-hor__destinos">
          <div class="tel-hor__destino tel-hor__destino--aberto">
            <span class="advup-rotulo">
              <i class="i-ph-sun-fill" aria-hidden="true" />
              {{ t(`${T}.ABERTO`) }}
            </span>
            <AdvupSelect
              v-model="aberto"
              :options="opcoes"
              :placeholder="t(`${T}.ESCOLHER`)"
              :rotulo="t(`${T}.ABERTO`)"
              min-width="100%"
            />
          </div>
          <div class="tel-hor__destino tel-hor__destino--fechado">
            <span class="advup-rotulo">
              <i class="i-ph-moon-stars-fill" aria-hidden="true" />
              {{ t(`${T}.FECHADO`) }}
            </span>
            <AdvupSelect
              v-model="fechado"
              :options="opcoes"
              :placeholder="t(`${T}.ESCOLHER`)"
              :rotulo="t(`${T}.FECHADO`)"
              min-width="100%"
            />
          </div>
        </div>

        <p
          v-if="(tentou && erroGeral) || erro"
          class="tel-hor__erro"
          role="alert"
        >
          <i class="i-ph-warning-octagon-fill" aria-hidden="true" />
          <span>{{ erro || erroGeral }}</span>
        </p>
      </form>

      <div class="advup-modal__rodape advup-modal__rodape--acoes">
        <button type="button" class="advup-btn--vazado" @click="emit('close')">
          {{ t('TELEFONIA.ROTEAMENTO.CANCELAR') }}
        </button>
        <button
          type="button"
          class="advup-btn-forte"
          :disabled="salvando"
          @click="salvar"
        >
          <i
            :class="
              salvando ? 'i-ph-circle-notch advup-gira' : 'i-ph-check-bold'
            "
            aria-hidden="true"
          />
          {{
            salvando
              ? t('TELEFONIA.ROTEAMENTO.SALVANDO')
              : editando
                ? t('TELEFONIA.ROTEAMENTO.SALVAR')
                : t('TELEFONIA.ROTEAMENTO.CRIAR')
          }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.tel-hor__corpo {
  display: flex;
  flex-direction: column;
  gap: var(--esp-5);
}
.tel-hor__campo {
  display: flex;
  flex-direction: column;
  gap: var(--esp-2);
  min-width: 0;
}
.tel-hor__faixas {
  display: flex;
  flex-direction: column;
  gap: var(--esp-2);
  margin: 0;
  padding: 0;
  list-style: none;
}
.tel-hor__faixa {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--esp-3);
  padding: var(--esp-3);
  border: 1px solid rgb(var(--slate-4));
  border-radius: var(--r-4);
  background: rgb(var(--slate-2));
}
.tel-hor__faixa--erro {
  border-color: rgb(var(--ruby-7));
}
.tel-hor__dias {
  display: flex;
  gap: 4px;
}
.tel-hor__dia {
  display: grid;
  place-content: center;
  width: 36px;
  height: 32px;
  border-radius: var(--r-2);
  font-size: var(--txt-nota);
  font-weight: var(--peso-forte);
  background: rgb(var(--solid-1));
  color: rgb(var(--slate-10));
  box-shadow: inset 0 0 0 1px rgb(var(--slate-5));
  cursor: pointer;
  touch-action: manipulation;
  transition:
    background var(--mov-1) var(--mov-curva),
    color var(--mov-1) var(--mov-curva),
    transform var(--mov-1) var(--mov-curva);
}
.tel-hor__dia:active {
  transform: scale(0.94);
}
.tel-hor__dia--sim {
  background: rgb(var(--teal-9));
  color: white;
  box-shadow: none;
}
.tel-hor__horas {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
  font-size: var(--txt-nota);
  color: rgb(var(--slate-11));
}
.tel-hor__horas .advup-campo {
  width: 108px;
  font-variant-numeric: tabular-nums;
}
.tel-hor__mais {
  align-self: flex-start;
}
.tel-hor__feriados {
  display: flex;
  flex-wrap: wrap;
  gap: var(--esp-2);
}
.tel-hor__feriado {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-1);
  padding: 4px 4px 4px var(--esp-2);
  border-radius: 999px;
  font-size: var(--txt-nota);
  font-weight: var(--peso-forte);
  background: rgb(var(--amber-3));
  color: rgb(var(--amber-11));
}
.tel-hor__feriado-x {
  display: grid;
  place-content: center;
  width: 20px;
  height: 20px;
  border-radius: 999px;
  cursor: pointer;
}
.tel-hor__feriado-x:hover {
  background: rgb(var(--amber-5));
}
.tel-hor__novo-feriado {
  display: flex;
  gap: var(--esp-2);
}
.tel-hor__data {
  width: 170px;
}
.tel-hor__destinos {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--esp-3);
}
.tel-hor__destino {
  display: flex;
  flex-direction: column;
  gap: var(--esp-2);
  padding: var(--esp-3);
  border-radius: var(--r-4);
}
.tel-hor__destino .advup-rotulo {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-1);
}
.tel-hor__destino--aberto {
  background: rgb(var(--teal-2));
  box-shadow: inset 0 0 0 1px rgb(var(--teal-5));
}
.tel-hor__destino--aberto i {
  color: rgb(var(--amber-9));
}
.tel-hor__destino--fechado {
  background: rgb(var(--slate-2));
  box-shadow: inset 0 0 0 1px rgb(var(--slate-5));
}
.tel-hor__destino--fechado i {
  color: rgb(var(--iris-9, var(--slate-10)));
}
.tel-hor__erro {
  display: flex;
  align-items: flex-start;
  gap: var(--esp-2);
  margin: 0;
  padding: var(--esp-2) var(--esp-3);
  border-radius: var(--r-3);
  background: rgb(var(--ruby-3));
  font-size: var(--txt-nota);
  line-height: var(--linha-solta);
  color: rgb(var(--ruby-12));
}
.tel-hor-faixa-enter-active,
.tel-hor-faixa-leave-active {
  transition: all var(--mov-2) var(--mov-curva);
}
.tel-hor-faixa-enter-from,
.tel-hor-faixa-leave-to {
  opacity: 0;
  transform: scale(0.96);
}
@media (max-width: 639px) {
  .tel-hor__destinos {
    grid-template-columns: 1fr;
  }
  .tel-hor__dia {
    width: 34px;
  }
}
</style>
